class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.querySelector('cart-drawer-recommendations')?.load();
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define(
    'cart-drawer-recommendations',
    class CartDrawerRecommendations extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.onClick.bind(this));
        if (this.closest('cart-drawer')?.classList.contains('active')) this.load();
      }

      async load() {
        if (!this.dataset.url || this.dataset.loading === 'true') return;
        if (this.dataset.loaded === this.dataset.productId) return;

        this.dataset.loading = 'true';
        this.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) throw new Error('Recommendation request failed');

          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const result = documentFragment.querySelector('.cart-drawer-recommendations__result');

          if (!result || !result.innerHTML.trim()) {
            this.hidden = true;
            this.dataset.loaded = this.dataset.productId;
            return;
          }

          this.replaceChildren(result);
          this.hidden = false;
          this.dataset.loaded = this.dataset.productId;
        } catch (error) {
          this.hidden = true;
          console.error(error);
        } finally {
          delete this.dataset.loading;
          this.removeAttribute('aria-busy');
        }
      }

      async onClick(event) {
        const button = event.target.closest('[data-cart-recommendation-add]');
        if (!button || !this.contains(button) || button.getAttribute('aria-disabled') === 'true') return;

        const cart = document.querySelector('cart-drawer');
        const errorMessage = this.querySelector('[data-cart-recommendation-error]');
        const spinner = button.querySelector('.loading__spinner');

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        spinner?.classList.remove('hidden');
        if (errorMessage) {
          errorMessage.hidden = true;
          errorMessage.textContent = '';
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', button.dataset.variantId);
        formData.append('quantity', '1');
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id),
        );
        formData.append('sections_url', window.location.pathname);
        config.body = formData;

        try {
          const response = await fetch(routes.cart_add_url, config);
          const parsedState = await response.json();

          if (!response.ok || parsedState.status) {
            throw new Error(parsedState.description || parsedState.message || 'Cart update failed');
          }

          cart.setActiveElement(button);
          cart.renderContents(parsedState);
        } catch (error) {
          if (errorMessage) {
            errorMessage.textContent = error.message;
            errorMessage.hidden = false;
          }
          console.error(error);
        } finally {
          if (button.isConnected) {
            button.removeAttribute('aria-disabled');
            button.classList.remove('loading');
            spinner?.classList.add('hidden');
          }
        }
      }
    },
  );
}
